import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React from 'react';
import classnames from 'classnames';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';

var mousenavigation = false;
var touchnavigation = false;
var touchEventFired = false;
var listeners = {
  mousedown: handleMouseDown,
  keydown: handleKeydown,
  touchstart: handleTouchStart
};

function removeAllListeners() {
  Object.keys(listeners).forEach(function (listenerType) {
    document.removeEventListener(listenerType, listeners[listenerType]);
  });
}

function addListener(listenerType) {
  document.addEventListener(listenerType, listeners[listenerType]);
}

function handleMouseDown() {
  if (!mousenavigation && !touchEventFired) {
    mousenavigation = true;
    touchnavigation = false;
    var htmlElement = document.querySelector("html");
    htmlElement === null || htmlElement === void 0 ? void 0 : htmlElement.setAttribute("data-mousenavigation", "true");
    htmlElement === null || htmlElement === void 0 ? void 0 : htmlElement.removeAttribute("data-touchnavigation"); // Reset listeners

    removeAllListeners();
    addListener("touchstart");
    addListener("keydown");
  }
}

function handleKeydown(event) {
  if (event.key === "Tab") {
    var htmlElement = document.querySelector("html");
    removeAllListeners();
    htmlElement === null || htmlElement === void 0 ? void 0 : htmlElement.removeAttribute("data-mousenavigation");
    htmlElement === null || htmlElement === void 0 ? void 0 : htmlElement.removeAttribute("data-touchnavigation");
    mousenavigation = false;
    touchnavigation = false; // Reset listeners

    addListener("touchstart");
    addListener("mousedown");
  }
}

function handleTouchStart() {
  if (!touchnavigation) {
    mousenavigation = false;
    touchnavigation = true;
    var htmlElement = document.querySelector("html");
    htmlElement === null || htmlElement === void 0 ? void 0 : htmlElement.setAttribute("data-touchnavigation", "true");
    htmlElement === null || htmlElement === void 0 ? void 0 : htmlElement.removeAttribute("data-mousenavigation"); // Reset listeners

    removeAllListeners();
    addListener("touchstart");
    addListener("keydown");
    addListener("mousedown");
  } // Most touch devices fire both touch and mouse events on touch (in that order)
  // see https://w3c.github.io/touch-events/#mouse-events
  // Set a check variable to avoid resetting the data-attribute:


  touchEventFired = true;
  setTimeout(function () {
    touchEventFired = false;
  }, 150); // Yes, it can take this long between the events
}

function initTabListener() {
  if (typeof document !== "undefined") {
    addListener("touchstart");
    addListener("mousedown");
  }
}

function getValuePair(item) {
  return typeof item === "string" ? {
    value: item,
    label: item
  } : item;
}

var breakpoints = {
  small: 768,
  medium: 992,
  large: 1200,
  xl: 1600
};

var Label = function Label(_ref) {
  var _classNames;

  var _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? "small" : _ref$variant,
      forceCompact = _ref.forceCompact,
      srOnly = _ref.srOnly,
      children = _ref.children,
      standAlone = _ref.standAlone,
      htmlFor = _ref.htmlFor,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? "" : _ref$className;
  var labelClassNames = classnames("jkl-label", className, (_classNames = {}, _defineProperty(_classNames, "jkl-label--".concat(variant), variant), _defineProperty(_classNames, "jkl-label--compact", forceCompact), _defineProperty(_classNames, "jkl-label--sr-only", srOnly), _classNames));
  var C = standAlone ? "label" : "span";

  if (!standAlone && htmlFor) {
    htmlFor = undefined;

    if (process.env.NODE_ENV !== "production") {
      console.warn("WARNING: The standard Label component renders as a <span> element, which does not take a htmlFor prop. If you want the Label to belong to a specific input, use the standAlone prop, which renders as a <label> element instead. In most cases the Label component should not be used directly, as it is part of all our input components.");
    }
  }

  return /*#__PURE__*/React.createElement(C, {
    className: labelClassNames,
    htmlFor: htmlFor
  }, children);
};

var _excluded$1 = ["negative", "external", "className", "children"];
var Link = function Link(_ref) {
  var _ref$negative = _ref.negative,
      negative = _ref$negative === void 0 ? false : _ref$negative,
      _ref$external = _ref.external,
      external = _ref$external === void 0 ? false : _ref$external,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? "" : _ref$className,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  return /*#__PURE__*/React.createElement("a", _extends({
    className: classnames("jkl-link", className, {
      "jkl-link--negative": negative,
      "jkl-link--external": external
    })
  }, rest), children);
};

var _excluded = ["inverted", "active", "back", "className", "children"];
var NavLink = function NavLink(_ref) {
  var _ref$inverted = _ref.inverted,
      inverted = _ref$inverted === void 0 ? false : _ref$inverted,
      _ref$active = _ref.active,
      active = _ref$active === void 0 ? false : _ref$active,
      _ref$back = _ref.back,
      back = _ref$back === void 0 ? false : _ref$back,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? "" : _ref$className,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement("a", _extends({
    className: classnames("jkl-nav-link", {
      "jkl-nav-link--inverted": inverted,
      "jkl-nav-link--active": active,
      "jkl-nav-link--back": back
    }, className)
  }, rest), children);
};

var ScreenReaderOnly = function ScreenReaderOnly(_ref) {
  var children = _ref.children,
      showOnFocus = _ref.showOnFocus;
  return /*#__PURE__*/React.createElement("span", {
    className: "jkl-sr-only ".concat(showOnFocus ? "jkl-sr-only--focusable" : "")
  }, children);
};

var SupportLabel = function SupportLabel(_ref) {
  var id = _ref.id,
      helpLabel = _ref.helpLabel,
      errorLabel = _ref.errorLabel,
      forceCompact = _ref.forceCompact,
      className = _ref.className,
      srOnly = _ref.srOnly,
      inverted = _ref.inverted;
  var componentClassName = classnames("jkl-form-support-label", className, {
    "jkl-form-support-label--compact": forceCompact,
    "jkl-form-support-label--error": errorLabel,
    "jkl-form-support-label--help": !errorLabel,
    "jkl-form-support-label--sr-only": srOnly,
    "jkl-form-support-label--inverted": inverted
  });

  if (errorLabel || helpLabel) {
    return /*#__PURE__*/React.createElement("span", {
      id: id,
      className: componentClassName
    }, errorLabel || helpLabel);
  }

  return null;
};

export { Label, Link, NavLink, ScreenReaderOnly, SupportLabel, breakpoints, getValuePair, initTabListener };
